// src/components/Admin/DiscoverCuration.js
import React, { useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  doc, 
  writeBatch,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db, auth } from '../../services/firebase';

console.log('Current user:', auth.currentUser?.email);

// Admin Image Upload Component with Enhanced AI Analysis
const AdminImageUpload = ({ onUploadComplete }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await handleFiles(files);
    }
  };

  const handleFileInput = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      await handleFiles(files);
    }
  };

  const handleFiles = async (files) => {
    setUploading(true);
    setUploadMessage('🔄 Processing images...');

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          errorCount++;
          continue;
        }

        try {
          await processImage(file);
          successCount++;
        } catch (error) {
          console.error('Error processing image:', error);
          errorCount++;
        }
      }

      setUploadMessage(`✅ Successfully uploaded ${successCount} images! ${errorCount > 0 ? `${errorCount} failed.` : ''}`);
      
      if (successCount > 0) {
        // Refresh the content list
        setTimeout(() => {
          onUploadComplete();
        }, 1000);
      }

    } catch (error) {
      console.error('Error in handleFiles:', error);
      setUploadMessage(`❌ Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const processImage = async (file) => {
    try {
      // Ensure user is authenticated
      if (!auth.currentUser) {
        throw new Error('User not authenticated');
      }

      console.log('🔄 Converting image to base64...');
      
      // Convert image to base64
      const imageData = await fileToBase64(file);
      const base64Data = imageData.split(',')[1]; // Remove data:image/jpeg;base64, prefix
      
      console.log('🔄 Uploading via Firebase Function...');
      
      // Upload via Firebase Function (bypasses CORS)
      const uploadResponse = await fetch('https://us-central1-a-r-t-i-f-a-c-t.cloudfunctions.net/uploadImage', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          imageData: base64Data,
          fileName: file.name,
          userId: auth.currentUser.email
        })
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        throw new Error(`Upload failed: ${uploadResponse.status}: ${errorText}`);
      }

      const uploadResult = await uploadResponse.json();
      
      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Upload failed');
      }

      const downloadURL = uploadResult.downloadURL;
      console.log('✅ Image uploaded successfully:', downloadURL);

      console.log('🔄 Analyzing with Enhanced Claude 4.0...');
      
      // Call Enhanced Claude analysis function
      const analysisResponse = await fetch('https://us-central1-a-r-t-i-f-a-c-t.cloudfunctions.net/analyzeImageEnhanced', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          imageData: base64Data,
          fileName: file.name,
          useAdvancedPrompt: true
        })
      });

      if (!analysisResponse.ok) {
        const errorText = await analysisResponse.text();
        throw new Error(`Enhanced Claude API returned ${analysisResponse.status}: ${errorText}`);
      }

      const result = await analysisResponse.json();
      console.log('✅ Enhanced Claude analysis result:', result);

      // Use Enhanced Claude's analysis or fallback
      const analysis = result.success ? result.analysis : result.fallback;
      
      // Save to Firestore as pending
      const docRef = doc(collection(db, 'discover'));
      const newItem = {
        title: analysis.title,
        description: analysis.description,
        imageUrl: downloadURL,
        source: 'uploaded by artifex',
        sourceUrl: '',
        category: analysis.category,
        tags: analysis.tags || [],
        signals: analysis.signals || [],
        status: 'pending',
        submissionType: 'admin_upload',
        aiAnalysis: {
          claudeEnhanced: result.success,
          analysisMethod: 'claude_4_enhanced',
          canEnhanceWithChatGPT: true,
          originalFileName: file.name
        },
        metadata: {
          originalFileName: file.name,
          fileSize: file.size,
          uploadedBy: auth.currentUser?.email || 'admin',
          claudeAnalysis: result.success,
          storageFileName: uploadResult.fileName
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await writeBatch(db).set(docRef, newItem).commit();
      console.log('✅ Saved to Firestore with pending status');
      
    } catch (error) {
      console.error('❌ Error in processImage:', error);
      throw error; // Re-throw to be caught by handleFiles
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  return (
    <div style={{
      marginBottom: '1.36rem',
      padding: '1.02rem',
      backgroundColor: '#f8f9fa',
      borderRadius: '5.44px',
      border: '1.36px dashed #dee2e6'
    }}>
      <h3 style={{ margin: '0 0 0.68rem 0', color: '#333' }}>📸 Enhanced AI Image Upload</h3>
      <p style={{ margin: '0 0 0.68rem 0', fontSize: '0.612rem', color: '#666' }}>
        🆕 <strong>Enhanced Claude 4.0</strong> analysis with detailed design descriptions + optional ChatGPT upgrade
      </p>
      
      <div
        style={{
          padding: '1.36rem',
          backgroundColor: dragActive ? '#e3f2fd' : 'white',
          border: dragActive ? '2px dashed #2196f3' : '2px dashed #ccc',
          borderRadius: '5.44px',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => document.getElementById('admin-file-input').click()}
      >
        <input
          id="admin-file-input"
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />
        
        {uploading ? (
          <div>
            <div style={{ fontSize: '1.36rem', marginBottom: '0.68rem' }}>⟳</div>
            <p style={{ margin: 0, color: '#666' }}>Processing with Enhanced Claude 4.0...</p>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: '2.04rem', marginBottom: '0.68rem' }}>📁</div>
            <p style={{ margin: '0 0 0.34rem 0', fontSize: '0.748rem', fontWeight: '600' }}>
              Drop images here or click to browse
            </p>
            <p style={{ margin: 0, color: '#666', fontSize: '0.612rem' }}>
              Enhanced Claude 4.0 analysis → pending approval → ChatGPT upgrade available
            </p>
          </div>
        )}
      </div>

      {uploadMessage && (
        <div style={{
          marginTop: '0.68rem',
          padding: '0.51rem',
          backgroundColor: uploadMessage.includes('❌') ? '#fee' : '#efe',
          color: uploadMessage.includes('❌') ? '#c33' : '#363',
          borderRadius: '4.08px',
          fontSize: '0.612rem'
        }}>
          {uploadMessage}
        </div>
      )}
    </div>
  );
};

// Enhanced Analysis Component for Individual Items
const EnhancedAnalysisPanel = ({ item, onAnalysisComplete }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [showCostWarning, setShowCostWarning] = useState(false);

  const handleEnhanceWithChatGPT = async () => {
    if (!showCostWarning) {
      setShowCostWarning(true);
      return;
    }

    setIsAnalyzing(true);
    setShowCostWarning(false);

    try {
      console.log('🔄 Enhancing with ChatGPT Vision...');
      
      const enhanceResponse = await fetch('https://us-central1-a-r-t-i-f-a-c-t.cloudfunctions.net/enhanceWithChatGPT', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          imageUrl: item.imageUrl,
          currentTitle: item.title,
          currentDescription: item.description,
          currentTags: item.tags || [],
          currentSignals: item.signals || [],
          itemId: item.id
        })
      });

      if (!enhanceResponse.ok) {
        const errorText = await enhanceResponse.text();
        throw new Error(`ChatGPT API returned ${enhanceResponse.status}: ${errorText}`);
      }

      const result = await enhanceResponse.json();
      console.log('✅ ChatGPT enhancement result:', result);

      if (result.success) {
        setAnalysisResult(result);
        
        // Update the item in Firebase
        const itemRef = doc(db, 'discover', item.id);
        await updateDoc(itemRef, {
          title: result.enhanced.title,
          description: result.enhanced.description,
          tags: result.enhanced.tags,
          signals: result.enhanced.signals,
          aiAnalysis: {
            ...item.aiAnalysis,
            chatGPTEnhanced: true,
            enhancementDate: serverTimestamp(),
            analysisMethod: 'chatgpt_enhanced'
          },
          updatedAt: serverTimestamp()
        });

        console.log('✅ Item updated with ChatGPT enhancement');
        onAnalysisComplete?.();
      } else {
        throw new Error(result.error || 'Enhancement failed');
      }

    } catch (error) {
      console.error('❌ Error enhancing with ChatGPT:', error);
      setAnalysisResult({ error: error.message });
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div style={{
      marginTop: '0.68rem',
      padding: '0.68rem',
      backgroundColor: '#f0f8ff',
      border: '1px solid #007bff',
      borderRadius: '4.08px'
    }}>
      <h4 style={{ margin: '0 0 0.51rem 0', color: '#007bff', fontSize: '0.612rem' }}>
        🚀 AI Enhancement Options
      </h4>
      
      <div style={{ marginBottom: '0.51rem', fontSize: '0.578rem', color: '#666' }}>
        <strong>Current Analysis:</strong> {item.aiAnalysis?.analysisMethod || 'claude_basic'}
        {item.aiAnalysis?.chatGPTEnhanced && (
          <span style={{ color: '#28a745', marginLeft: '0.34rem', fontWeight: '600' }}>
            ✅ ChatGPT Enhanced
          </span>
        )}
      </div>

      {!item.aiAnalysis?.chatGPTEnhanced && (
        <div>
          {!showCostWarning ? (
            <button
              onClick={handleEnhanceWithChatGPT}
              disabled={isAnalyzing}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: isAnalyzing ? '#ccc' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '2.72px',
                cursor: isAnalyzing ? 'not-allowed' : 'pointer',
                fontSize: '0.544rem',
                marginRight: '0.5rem'
              }}
            >
              {isAnalyzing ? '⟳ Enhancing...' : '🔥 Enhance with ChatGPT Vision'}
            </button>
          ) : (
            <div style={{ 
              padding: '0.51rem', 
              backgroundColor: '#fff3cd', 
              border: '1px solid #ffc107',
              borderRadius: '2.72px',
              marginBottom: '0.51rem'
            }}>
              <p style={{ margin: '0 0 0.34rem 0', fontSize: '0.578rem', color: '#856404' }}>
                <strong>💰 Cost: $0.01</strong> - This will use ChatGPT Vision API for detailed analysis.
              </p>
              <div style={{ display: 'flex', gap: '0.34rem' }}>
                <button
                  onClick={handleEnhanceWithChatGPT}
                  disabled={isAnalyzing}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '2.72px',
                    cursor: 'pointer',
                    fontSize: '0.544rem'
                  }}
                >
                  ✅ Proceed ($0.01)
                </button>
                <button
                  onClick={() => setShowCostWarning(false)}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '2.72px',
                    cursor: 'pointer',
                    fontSize: '0.544rem'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {analysisResult && (
        <div style={{
          marginTop: '0.68rem',
          padding: '0.51rem',
          backgroundColor: analysisResult.error ? '#fee' : '#efe',
          color: analysisResult.error ? '#c33' : '#363',
          borderRadius: '2.72px',
          fontSize: '0.578rem'
        }}>
          {analysisResult.error ? (
            `❌ Enhancement failed: ${analysisResult.error}`
          ) : (
            <div>
              <strong>✅ Enhanced with ChatGPT!</strong>
              <br />
              <strong>Cost:</strong> $0.01 • <strong>New Analysis:</strong> {analysisResult.enhanced.title.substring(0, 50)}...
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Inline Edit Component for titles and descriptions
const InlineEdit = ({ 
  value, 
  onSave, 
  onCancel, 
  multiline = false, 
  placeholder = "Click to edit...",
  fieldName = "field"
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleStartEdit = () => {
    setEditValue(value || '');
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (editValue.trim() === (value || '').trim()) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(editValue.trim());
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving:', error);
      alert(`Error saving ${fieldName}: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value || '');
    setIsEditing(false);
    if (onCancel) onCancel();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Enter' && e.ctrlKey && multiline) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (!isEditing) {
    return (
      <div
        onClick={handleStartEdit}
        style={{
          cursor: 'pointer',
          padding: '0.17rem',
          borderRadius: '2.72px',
          border: '1px solid transparent',
          minHeight: multiline ? '2.04rem' : '1.02rem',
          backgroundColor: 'transparent',
          transition: 'all 0.2s ease',
          position: 'relative'
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#f8f9fa';
          e.target.style.border = '1px solid #dee2e6';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = 'transparent';
          e.target.style.border = '1px solid transparent';
        }}
      >
        {value || (
          <span style={{ color: '#999', fontStyle: 'italic' }}>
            {placeholder}
          </span>
        )}
        <span style={{
          position: 'absolute',
          top: '0.17rem',
          right: '0.17rem',
          fontSize: '0.476rem',
          color: '#999',
          opacity: 0,
          transition: 'opacity 0.2s ease'
        }}>
          ✏️
        </span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.34rem' }}>
      {multiline ? (
        <textarea
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
          style={{
            width: '100%',
            minHeight: '2.72rem',
            padding: '0.34rem',
            border: '2px solid #007bff',
            borderRadius: '2.72px',
            fontSize: '0.612rem',
            fontFamily: 'inherit',
            resize: 'vertical'
          }}
          placeholder={placeholder}
        />
      ) : (
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
          style={{
            width: '100%',
            padding: '0.34rem',
            border: '2px solid #007bff',
            borderRadius: '2.72px',
            fontSize: '0.612rem',
            fontFamily: 'inherit'
          }}
          placeholder={placeholder}
        />
      )}
      
      <div style={{ display: 'flex', gap: '0.34rem', justifyContent: 'flex-end' }}>
        <button
          onClick={handleSave}
          disabled={isSaving}
          style={{
            padding: '0.17rem 0.51rem',
            backgroundColor: isSaving ? '#ccc' : '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '2.72px',
            cursor: isSaving ? 'not-allowed' : 'pointer',
            fontSize: '0.544rem'
          }}
        >
          {isSaving ? '💾...' : '💾 Save'}
        </button>
        
        <button
          onClick={handleCancel}
          disabled={isSaving}
          style={{
            padding: '0.17rem 0.51rem',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '2.72px',
            cursor: 'pointer',
            fontSize: '0.544rem'
          }}
        >
          ❌ Cancel
        </button>
      </div>
      
      {multiline && (
        <div style={{ fontSize: '0.476rem', color: '#666' }}>
          Tip: Press Ctrl+Enter to save, Escape to cancel
        </div>
      )}
    </div>
  );
};

const DiscoverCuration = () => {
  const [pendingContent, setPendingContent] = useState([]);
  const [approvedContent, setApprovedContent] = useState([]);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [sortBy, setSortBy] = useState('newest');
  const [sourceFilter, setSourceFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [redundancies, setRedundancies] = useState([]);
  const [dismissedRedundancies, setDismissedRedundancies] = useState(new Set());

   const debugAuth = async () => {
    if (auth.currentUser) {
      try {
        const token = await auth.currentUser.getIdTokenResult();
        console.log('🔑 User token claims:', token.claims);
        console.log('🔑 Admin claim:', token.claims.admin);
        console.log('🔑 Email:', token.claims.email);
        
        if (!token.claims.admin) {
          console.warn('⚠️ USER LACKS ADMIN CLAIMS - Firestore writes will fail!');
          console.log('🔧 Need to set admin custom claims for this user');
        }
      } catch (error) {
        console.error('❌ Error getting token:', error);
      }
    } else {
      console.warn('⚠️ NO USER AUTHENTICATED');
    }
  };

  // Add this useEffect to call debugAuth when component loads:
  useEffect(() => {
    debugAuth();
  }, []);

  const categories = ['architecture', 'interiors', 'furniture', 'art'];
  const sources = ['from the archives of The Metropolitan Museum of Art', 'from the archives of Smithsonian Institution', 'uploaded by artifex', 'User Submission'];

  // Calculate Levenshtein distance
  const levenshteinDistance = useCallback((str1, str2) => {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  }, []);

  // String similarity using Levenshtein distance
  const stringSimilarity = useCallback((str1, str2) => {
    if (!str1 || !str2) return 0;
    
    const s1 = str1.toLowerCase().trim();
    const s2 = str2.toLowerCase().trim();
    
    if (s1 === s2) return 1;
    
    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;
    
    if (longer.length === 0) return 1;
    
    const distance = levenshteinDistance(longer, shorter);
    return (longer.length - distance) / longer.length;
  }, [levenshteinDistance]);

  // Calculate set overlap percentage
  const calculateSetOverlap = useCallback((set1, set2) => {
    if (set1.size === 0 && set2.size === 0) return 0;
    
    const intersection = new Set([...set1].filter(x => set2.has(x)));
    const union = new Set([...set1, ...set2]);
    
    return intersection.size / union.size;
  }, []);

  // Calculate similarity between two content items
  const calculateSimilarity = useCallback((item1, item2) => {
    let score = 0;
    const reasons = [];
    const maxScore = 10;

    // Title similarity (3 points max)
    const titleSim = stringSimilarity(item1.title || '', item2.title || '');
    if (titleSim > 0.8) {
      score += 3;
      reasons.push(`Very similar titles (${Math.round(titleSim * 100)}% match)`);
    } else if (titleSim > 0.6) {
      score += 2;
      reasons.push(`Similar titles (${Math.round(titleSim * 100)}% match)`);
    } else if (titleSim > 0.4) {
      score += 1;
      reasons.push(`Somewhat similar titles (${Math.round(titleSim * 100)}% match)`);
    }

    // Description similarity (2 points max)
    const descSim = stringSimilarity(item1.description || '', item2.description || '');
    if (descSim > 0.7) {
      score += 2;
      reasons.push(`Very similar descriptions (${Math.round(descSim * 100)}% match)`);
    } else if (descSim > 0.5) {
      score += 1;
      reasons.push(`Similar descriptions (${Math.round(descSim * 100)}% match)`);
    }

    // Same source (1 point)
    if (item1.source === item2.source && item1.source) {
      score += 1;
      reasons.push(`Same source: ${item1.source}`);
    }

    // Tag overlap (2 points max)
    const tags1 = new Set((item1.tags || []).map(t => t.toLowerCase()));
    const tags2 = new Set((item2.tags || []).map(t => t.toLowerCase()));
    const tagOverlap = calculateSetOverlap(tags1, tags2);
    
    if (tagOverlap > 0.7) {
      score += 2;
      reasons.push(`High tag overlap (${Math.round(tagOverlap * 100)}%)`);
    } else if (tagOverlap > 0.4) {
      score += 1;
      reasons.push(`Moderate tag overlap (${Math.round(tagOverlap * 100)}%)`);
    }

    // Signal overlap (2 points max)
    const signals1 = new Set((item1.signals || []).map(s => s.toLowerCase()));
    const signals2 = new Set((item2.signals || []).map(s => s.toLowerCase()));
    const signalOverlap = calculateSetOverlap(signals1, signals2);
    
    if (signalOverlap > 0.7) {
      score += 2;
      reasons.push(`High signal overlap (${Math.round(signalOverlap * 100)}%)`);
    } else if (signalOverlap > 0.4) {
      score += 1;
      reasons.push(`Moderate signal overlap (${Math.round(signalOverlap * 100)}%)`);
    }

    const normalizedScore = score / maxScore;

    return {
      score: normalizedScore,
      reasons: reasons,
      rawScore: score,
      maxScore: maxScore
    };
  }, [stringSimilarity, calculateSetOverlap]);

  // Advanced redundancy detection algorithm
  const detectRedundancies = useCallback((contentItems) => {
    const redundancyGroups = [];
    const processed = new Set();

    contentItems.forEach((item, index) => {
      if (processed.has(item.id)) return;

      const similarItems = [];
      
      contentItems.forEach((otherItem, otherIndex) => {
        if (index === otherIndex || processed.has(otherItem.id)) return;

        const similarity = calculateSimilarity(item, otherItem);
        
        if (similarity.score >= 0.7) {
          similarItems.push({
            item: otherItem,
            similarity: similarity
          });
        }
      });

      if (similarItems.length > 0) {
        processed.add(item.id);
        similarItems.forEach(sim => processed.add(sim.item.id));

        redundancyGroups.push({
          id: `group_${Date.now()}_${index}`,
          primary: item,
          duplicates: similarItems,
          reasons: similarItems[0]?.similarity.reasons || [],
          maxScore: Math.max(...similarItems.map(s => s.similarity.score)),
          groupSize: similarItems.length + 1
        });
      }
    });

    return redundancyGroups;
  }, [calculateSimilarity]);

  // Load content data from Firebase - FIXED TO USE REAL DOCUMENT IDS
  const loadContentData = useCallback(async () => {
    setIsLoading(true);
    try {
      console.log('🔄 Loading content from Firebase...');
      
      // Load pending content
      const pendingQuery = query(
        collection(db, 'discover'),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      );
      
      const pendingSnapshot = await getDocs(pendingQuery);
      const pendingItems = [];
      
      pendingSnapshot.forEach(doc => {
        const data = doc.data();
        // CRITICAL FIX: Use doc.id (real Firebase ID) and manually map fields
        pendingItems.push({
          id: doc.id, // Real Firebase document ID - DO NOT OVERWRITE
          title: data.title || '',
          description: data.description || '',
          imageUrl: data.imageUrl || '',
          source: data.source || '',
          sourceUrl: data.sourceUrl || '',
          category: data.category || '',
          tags: data.tags || [],
          signals: data.signals || [],
          status: data.status || 'pending',
          objectID: data.objectID || '',
          aiAnalysis: data.aiAnalysis || {},
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt || Date.now()),
          approvedAt: data.approvedAt || null,
          // Legacy compatibility fields
          caption: data.title || data.caption || '',
          image_url: data.imageUrl || data.image_url || '',
          user_or_vendor: data.source || data.user_or_vendor || ''
        });
      });

      // Load approved content
      const approvedQuery = query(
        collection(db, 'discover'),
        where('status', '==', 'approved'),
        orderBy('createdAt', 'desc')
      );
      
      const approvedSnapshot = await getDocs(approvedQuery);
      const approvedItems = [];
      
      approvedSnapshot.forEach(doc => {
        const data = doc.data();
        // CRITICAL FIX: Use doc.id (real Firebase ID) and manually map fields
        approvedItems.push({
          id: doc.id, // Real Firebase document ID - DO NOT OVERWRITE
          title: data.title || '',
          description: data.description || '',
          imageUrl: data.imageUrl || '',
          source: data.source || '',
          sourceUrl: data.sourceUrl || '',
          category: data.category || '',
          tags: data.tags || [],
          signals: data.signals || [],
          status: data.status || 'approved',
          objectID: data.objectID || '',
          aiAnalysis: data.aiAnalysis || {},
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt || Date.now()),
          approvedAt: data.approvedAt || null,
          // Legacy compatibility fields
          caption: data.title || data.caption || '',
          image_url: data.imageUrl || data.image_url || '',
          user_or_vendor: data.source || data.user_or_vendor || ''
        });
      });

      console.log('📊 Real Firebase IDs loaded:');
      console.log('📊 Pending items:', pendingItems.map(item => `${item.id} (${item.title?.substring(0, 30)}...)`));
      console.log('📊 Approved items:', approvedItems.map(item => `${item.id} (${item.title?.substring(0, 30)}...)`));

      setPendingContent(pendingItems);
      setApprovedContent(approvedItems);

      // Detect redundancies in pending content
      const detectedRedundancies = detectRedundancies(pendingItems);
      setRedundancies(detectedRedundancies);

      console.log('✅ Firebase data loaded with REAL document IDs:', {
        pending: pendingItems.length,
        approved: approvedItems.length,
        redundancies: detectedRedundancies.length
      });

      setMessage(`✅ Loaded ${pendingItems.length} pending and ${approvedItems.length} approved items from Firebase. Found ${detectedRedundancies.length} potential redundancy groups.`);
    } catch (error) {
      console.error('❌ Error loading content from Firebase:', error);
      setMessage(`❌ Error loading content from Firebase: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [detectRedundancies]);

  useEffect(() => {
    loadContentData();
  }, [loadContentData]);

  // Handle inline editing save for title and description
  const handleEditSave = async (itemId, field, newValue) => {
    try {
      console.log(`🔄 Updating ${field} for item ${itemId}:`, newValue);
      
      const itemRef = doc(db, 'discover', itemId);
      await updateDoc(itemRef, {
        [field]: newValue,
        updatedAt: serverTimestamp()
      });
      
      // Update local state optimistically
      if (activeTab === 'approved') {
        setApprovedContent(prev => prev.map(item => 
          item.id === itemId 
            ? { ...item, [field]: newValue }
            : item
        ));
      } else {
        setPendingContent(prev => prev.map(item => 
          item.id === itemId 
            ? { ...item, [field]: newValue }
            : item
        ));
      }
      
      console.log(`✅ Successfully updated ${field} for item ${itemId}`);
      setMessage(`✅ Successfully updated ${field}!`);
      
    } catch (error) {
      console.error(`❌ Error updating ${field}:`, error);
      throw error; // Re-throw so InlineEdit can handle it
    }
  };

  // Filter and sort content
  const getFilteredContent = useCallback(() => {
    let content;
    
    if (activeTab === 'redundancies') {
      content = redundancies.filter(group => !dismissedRedundancies.has(group.id));
    } else {
      content = activeTab === 'pending' ? pendingContent : approvedContent;
      
      if (sourceFilter) {
        content = content.filter(item => item.source === sourceFilter);
      }
      
      if (categoryFilter) {
        content = content.filter(item => item.category === categoryFilter);
      }
    }
    
    if (activeTab !== 'redundancies') {
      const sorted = [...content].sort((a, b) => {
        switch (sortBy) {
          case 'newest':
            return (b.createdAt?.getTime?.() || 0) - (a.createdAt?.getTime?.() || 0);
          case 'oldest':
            return (a.createdAt?.getTime?.() || 0) - (b.createdAt?.getTime?.() || 0);
          case 'title':
            return (a.title || '').localeCompare(b.title || '');
          case 'source':
            return (a.source || '').localeCompare(b.source || '');
          case 'category':
            return (a.category || '').localeCompare(b.category || '');
          default:
            return 0;
        }
      });
      return sorted;
    } else {
      return [...content].sort((a, b) => b.maxScore - a.maxScore);
    }
  }, [activeTab, pendingContent, approvedContent, redundancies, dismissedRedundancies, sourceFilter, categoryFilter, sortBy]);

  const filteredContent = getFilteredContent();

  // Approve content items (move from pending to approved) - FIXED TO USE REAL IDS
  const handleApproveItems = async (itemIds) => {
    if (itemIds.length === 0) {
      setMessage('❌ No items selected');
      return;
    }

    if (!window.confirm(`Approve ${itemIds.length} items and move to live discover feed?`)) {
      return;
    }

    setIsLoading(true);
    try {
      console.log('🔄 Approving items with REAL Firebase IDs:', itemIds);
      
      const batch = writeBatch(db);
      
      // Update each item to approved status using REAL Firebase document IDs
      for (const itemId of itemIds) {
        console.log(`🔄 Updating document with ID: ${itemId}`);
        const itemRef = doc(db, 'discover', itemId);
        batch.update(itemRef, {
          status: 'approved',
          approvedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      
      await batch.commit();
      
      console.log('✅ Items approved successfully using real Firebase IDs');
      setMessage(`✅ Successfully approved ${itemIds.length} items! They are now live on the discover feed.`);
      setSelectedItems(new Set());
      
      // Refresh data
      await loadContentData();
      
    } catch (error) {
      console.error('❌ Error approving items:', error);
      setMessage(`❌ Error approving items: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Reject content items (remove from pending) - FIXED TO USE REAL IDS
  const handleRejectItems = async (itemIds) => {
    if (itemIds.length === 0) {
      setMessage('❌ No items selected');
      return;
    }

    if (!window.confirm(`Permanently reject ${itemIds.length} items? This cannot be undone.`)) {
      return;
    }

    setIsLoading(true);
    try {
      console.log('🔄 Rejecting items with REAL Firebase IDs:', itemIds);
      
      const batch = writeBatch(db);
      
      // Delete each item from Firebase using REAL document IDs
      for (const itemId of itemIds) {
        console.log(`🔄 Deleting document with ID: ${itemId}`);
        const itemRef = doc(db, 'discover', itemId);
        batch.delete(itemRef);
      }
      
      await batch.commit();
      
      console.log('✅ Items rejected successfully using real Firebase IDs');
      setMessage(`✅ Successfully rejected ${itemIds.length} items! They have been permanently removed.`);
      setSelectedItems(new Set());
      
      // Refresh data
      await loadContentData();
      
    } catch (error) {
      console.error('❌ Error rejecting items:', error);
      setMessage(`❌ Error rejecting items: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete approved content items - FIXED TO USE REAL IDS
  const handleDeleteItems = async (itemIds) => {
    if (itemIds.length === 0) {
      setMessage('❌ No items selected');
      return;
    }

    if (!window.confirm(`Permanently delete ${itemIds.length} approved items? This will remove them from the live feed and cannot be undone.`)) {
      return;
    }

    setIsLoading(true);
    try {
      console.log('🔄 Deleting items with REAL Firebase IDs:', itemIds);
      
      const batch = writeBatch(db);
      
      // Delete each item from Firebase using REAL document IDs
      for (const itemId of itemIds) {
        console.log(`🔄 Deleting document with ID: ${itemId}`);
        const itemRef = doc(db, 'discover', itemId);
        batch.delete(itemRef);
      }
      
      await batch.commit();
      
      console.log('✅ Items deleted successfully using real Firebase IDs');
      setMessage(`✅ Successfully deleted ${itemIds.length} approved items! They have been removed from the live feed.`);
      setSelectedItems(new Set());
      
      // Refresh data
      await loadContentData();
      
    } catch (error) {
      console.error('❌ Error deleting items:', error);
      setMessage(`❌ Error deleting items: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Archive content items - FIXED TO USE REAL IDS
  const handleArchiveItems = async (itemIds) => {
    if (itemIds.length === 0) {
      setMessage('❌ No items selected');
      return;
    }

    if (!window.confirm(`Archive ${itemIds.length} items? They will be hidden from the live feed but can be restored later.`)) {
      return;
    }

    setIsLoading(true);
    try {
      console.log('🔄 Archiving items with REAL Firebase IDs:', itemIds);
      
      const batch = writeBatch(db);
      
      // Update each item to archived status using REAL document IDs
      for (const itemId of itemIds) {
        console.log(`🔄 Archiving document with ID: ${itemId}`);
        const itemRef = doc(db, 'discover', itemId);
        batch.update(itemRef, {
          status: 'archived',
          archivedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      
      await batch.commit();
      
      console.log('✅ Items archived successfully using real Firebase IDs');
      setMessage(`✅ Successfully archived ${itemIds.length} items! They are now hidden from the live feed.`);
      setSelectedItems(new Set());
      
      // Refresh data
      await loadContentData();
      
    } catch (error) {
      console.error('❌ Error archiving items:', error);
      setMessage(`❌ Error archiving items: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle redundancy group actions - FIXED TO USE REAL IDS
  const handleDismissRedundancyGroup = (groupId) => {
    setDismissedRedundancies(prev => new Set([...prev, groupId]));
    setMessage('✅ Redundancy group dismissed');
  };

  const handleResolveRedundancy = async (group, action, selectedItemId = null) => {
    setIsLoading(true);
    try {
      console.log('🔄 Resolving redundancy with REAL Firebase IDs:', { action, selectedItemId });
      
      const batch = writeBatch(db);
      let itemsToRemove = [];
      let itemsToApprove = [];

      if (action === 'keep-primary') {
        itemsToRemove = group.duplicates.map(d => d.item.id);
        itemsToApprove = [group.primary.id];
      } else if (action === 'keep-selected' && selectedItemId) {
        itemsToRemove = [group.primary.id, ...group.duplicates.map(d => d.item.id)]
          .filter(id => id !== selectedItemId);
        itemsToApprove = [selectedItemId];
      } else if (action === 'delete-all') {
        itemsToRemove = [group.primary.id, ...group.duplicates.map(d => d.item.id)];
      }

      // Remove items using REAL Firebase document IDs
      for (const itemId of itemsToRemove) {
        console.log(`🔄 Removing document with ID: ${itemId}`);
        const itemRef = doc(db, 'discover', itemId);
        batch.delete(itemRef);
      }
      
      // Approve selected items using REAL Firebase document IDs
      for (const itemId of itemsToApprove) {
        console.log(`🔄 Approving document with ID: ${itemId}`);
        const itemRef = doc(db, 'discover', itemId);
        batch.update(itemRef, {
          status: 'approved',
          approvedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      
      await batch.commit();

      // Dismiss this redundancy group
      setDismissedRedundancies(prev => new Set([...prev, group.id]));
      
      console.log('✅ Redundancy resolved successfully using real Firebase IDs');
      setMessage(`✅ Redundancy resolved: ${itemsToRemove.length} removed, ${itemsToApprove.length} approved.`);
      
      // Refresh data
      await loadContentData();

    } catch (error) {
      console.error('❌ Error resolving redundancy:', error);
      setMessage(`❌ Error resolving redundancy: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle item selection
  const handleItemSelect = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleSelectAll = () => {
    if (activeTab === 'redundancies') return;
    
    if (selectedItems.size === filteredContent.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredContent.map(item => item.id)));
    }
  };

  // Run new content fetch using Firebase Function
  const handleFetchNewContent = async () => {
    if (!window.confirm('Fetch new content from museum APIs? This may take a few minutes.')) {
      return;
    }

    setIsLoading(true);
    setMessage('🔄 Fetching new content from museum APIs...');
    
    try {
      // Get existing item IDs to prevent duplicates
      const allExistingQuery = query(collection(db, 'discover'));
      const allExistingSnapshot = await getDocs(allExistingQuery);
      const existingIds = [];
      
      allExistingSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.sourceUrl) {
          existingIds.push(data.sourceUrl);
        }
        if (data.objectID) {
          existingIds.push(data.objectID.toString());
        }
      });

      console.log('🔄 Calling Firebase Function with existingIds:', existingIds.length);
      
      // Call Firebase Function with existing IDs
      const response = await fetch('https://us-central1-a-r-t-i-f-a-c-t.cloudfunctions.net/fetchDiscoverContent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          targetCount: 20,
          category: categoryFilter || 'all',
          sources: ['met', 'smithsonian'],
          existingIds: existingIds
        })
      });

      const result = await response.json();

      if (result.success && result.items) {
        console.log('✅ Firebase Function returned:', result.items.length, 'items');
        
        // Add items directly to Firebase with pending status
        const batch = writeBatch(db);
        let addedCount = 0;
        
        for (const item of result.items) {
          const docRef = doc(collection(db, 'discover'));
          batch.set(docRef, {
            ...item,
            status: 'pending',
            aiAnalysis: {
              analysisMethod: 'museum_api',
              canEnhanceWithChatGPT: true
            },
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
          addedCount++;
        }
        
        if (addedCount > 0) {
          await batch.commit();
          console.log('✅ Added', addedCount, 'items to Firebase');
        }
        
        setMessage(`✅ Successfully fetched and added ${addedCount} new items to pending approval! ` +
          `Met: ${result.summary?.metCount || 0}, Smithsonian: ${result.summary?.smithsonianCount || 0}.`);
          
        // Refresh data to show new items
        await loadContentData();
        
      } else {
        throw new Error(result.error || 'Failed to fetch content');
      }
      
    } catch (error) {
      console.error('❌ Error fetching content:', error);
      setMessage(`❌ Error fetching new content: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getRedundancyCount = () => {
    return redundancies.filter(group => !dismissedRedundancies.has(group.id)).length;
  };

  return (
    <div style={{
      maxWidth: '952px',
      margin: '1.36rem auto',
      padding: '1.36rem',
      backgroundColor: 'white',
      borderRadius: '8.16px',
      boxShadow: '0 2.72px 8.16px rgba(0, 0, 0, 0.08)'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.36rem' }}>
        <div>
          <h2 style={{ margin: 0, color: '#333' }}>Enhanced Discover Curation</h2>
          <p style={{ margin: '0.34rem 0 0', color: '#666', fontSize: '0.612rem' }}>
            Review and approve content with Enhanced Claude 4.0 + ChatGPT Vision backup
          </p>
          <p style={{ margin: '0.17rem 0 0', color: '#28a745', fontSize: '0.544rem', fontWeight: '600' }}>
            🔥 Firebase System: All changes persist automatically to your database!
          </p>
          <p style={{ margin: '0.17rem 0 0', color: '#007bff', fontSize: '0.544rem', fontWeight: '600' }}>
            ✏️ Inline Editing + 🚀 ChatGPT Enhancement: Click titles/descriptions to edit, use ChatGPT for richer analysis
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '0.68rem', alignItems: 'center' }}>
          <button
            onClick={() => window.location.href = '/admin/products'}
            style={{
              padding: '0.51rem 1.02rem',
              backgroundColor: '#6f42c1',
              color: 'white',
              border: 'none',
              borderRadius: '4.08px',
              cursor: 'pointer',
              fontSize: '0.612rem'
            }}
          >
            🛍️ Product Curation
          </button>
          
          <button
            onClick={handleFetchNewContent}
            disabled={isLoading}
            style={{
              padding: '0.51rem 1.02rem',
              backgroundColor: isLoading ? '#ccc' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4.08px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '0.612rem'
            }}
          >
            {isLoading ? '⟳ Fetching...' : '📥 Fetch New Content'}
          </button>
          
          <button
            onClick={loadContentData}
            disabled={isLoading}
            style={{
              padding: '0.51rem 1.02rem',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '4.08px',
              cursor: 'pointer',
              fontSize: '0.612rem'
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Enhanced Admin Image Upload Section */}
      <AdminImageUpload onUploadComplete={loadContentData} />

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        borderBottom: '1.36px solid #e9ecef',
        marginBottom: '1.36rem'
      }}>
        <button
          onClick={() => setActiveTab('pending')}
          style={{
           padding: '0.68rem 1.36rem',
           backgroundColor: 'transparent',
           border: 'none',
           borderBottom: activeTab === 'pending' ? '1.36px solid #007bff' : '1.36px solid transparent',
           color: activeTab === 'pending' ? '#007bff' : '#666',
           cursor: 'pointer',
           fontSize: '0.68rem',
           fontWeight: '600'
         }}
       >
         Pending Approval ({pendingContent.length})
       </button>
       
       <button
         onClick={() => setActiveTab('approved')}
         style={{
           padding: '0.68rem 1.36rem',
           backgroundColor: 'transparent',
           border: 'none',
           borderBottom: activeTab === 'approved' ? '1.36px solid #007bff' : '1.36px solid transparent',
           color: activeTab === 'approved' ? '#007bff' : '#666',
           cursor: 'pointer',
           fontSize: '0.68rem',
           fontWeight: '600'
         }}
       >
         Approved Content ({approvedContent.length})
       </button>
       
       <button
         onClick={() => setActiveTab('redundancies')}
         style={{
           padding: '0.68rem 1.36rem',
           backgroundColor: 'transparent',
           border: 'none',
           borderBottom: activeTab === 'redundancies' ? '1.36px solid #ffc107' : '1.36px solid transparent',
           color: activeTab === 'redundancies' ? '#ffc107' : '#666',
           cursor: 'pointer',
           fontSize: '0.68rem',
           fontWeight: '600',
           position: 'relative'
         }}
       >
         🚨 Redundancies ({getRedundancyCount()})
         {getRedundancyCount() > 0 && (
           <span style={{
             position: 'absolute',
             top: '0.34rem',
             right: '0.34rem',
             backgroundColor: '#dc3545',
             color: 'white',
             borderRadius: '50%',
             width: '13.6px',
             height: '13.6px',
             display: 'flex',
             alignItems: 'center',
             justifyContent: 'center',
             fontSize: '0.51rem',
             fontWeight: 'bold'
           }}>
             !
           </span>
         )}
       </button>
     </div>

     {/* Filters - only show for pending/approved tabs */}
     {activeTab !== 'redundancies' && (
       <div style={{ 
         display: 'flex', 
         gap: '0.68rem', 
         marginBottom: '1.02rem', 
         padding: '0.68rem', 
         backgroundColor: '#f8f9fa', 
         borderRadius: '5.44px',
         flexWrap: 'wrap'
       }}>
         <div style={{ flex: 1, minWidth: '102px' }}>
           <label style={{ display: 'block', marginBottom: '0.17rem', fontSize: '0.612rem', fontWeight: '600' }}>
             Source Filter
           </label>
           <select
             value={sourceFilter}
             onChange={(e) => setSourceFilter(e.target.value)}
             style={{
               width: '100%',
               padding: '0.34rem',
               border: '0.68px solid #ddd',
               borderRadius: '2.72px',
               fontSize: '0.612rem'
             }}
           >
             <option value="">All Sources</option>
             {sources.map(source => (
               <option key={source} value={source}>{source}</option>
             ))}
           </select>
         </div>
         
         <div style={{ flex: 1, minWidth: '102px' }}>
           <label style={{ display: 'block', marginBottom: '0.17rem', fontSize: '0.612rem', fontWeight: '600' }}>
             Category Filter
           </label>
           <select
             value={categoryFilter}
             onChange={(e) => setCategoryFilter(e.target.value)}
             style={{
               width: '100%',
               padding: '0.34rem',
               border: '1px solid #ddd',
               borderRadius: '2.72px',
               fontSize: '0.612rem'
             }}
           >
             <option value="">All Categories</option>
             {categories.map(cat => (
               <option key={cat} value={cat}>{cat}</option>
             ))}
           </select>
         </div>
         
         <div style={{ flex: 1, minWidth: '102px' }}>
           <label style={{ display: 'block', marginBottom: '0.17rem', fontSize: '0.612rem', fontWeight: '600' }}>
             Sort By
           </label>
           <select
             value={sortBy}
             onChange={(e) => setSortBy(e.target.value)}
             style={{
               width: '100%',
               padding: '0.34rem',
               border: '1px solid #ddd',
               borderRadius: '2.72px',
               fontSize: '0.612rem'
             }}
           >
             <option value="newest">Newest First</option>
             <option value="oldest">Oldest First</option>
             <option value="title">Title A-Z</option>
             <option value="source">Source A-Z</option>
             <option value="category">Category A-Z</option>
           </select>
         </div>
       </div>
     )}

     {/* Bulk Actions - only show for pending/approved tabs */}
     {activeTab !== 'redundancies' && (
       <div style={{ 
         display: 'flex', 
         justifyContent: 'space-between', 
         alignItems: 'center', 
         marginBottom: '0.68rem',
         padding: '0.68rem',
         backgroundColor: '#e9ecef',
         borderRadius: '4.08px'
       }}>
         <div>
           <button
             onClick={handleSelectAll}
             style={{
               padding: '0.5rem 1rem',
               backgroundColor: '#6c757d',
               color: 'white',
               border: 'none',
               borderRadius: '2.72px',
               cursor: 'pointer',
               fontSize: '0.612rem',
               marginRight: '0.68rem'
             }}
           >
             {selectedItems.size === filteredContent.length ? 'Deselect All' : 'Select All'}
           </button>
           
           <span style={{ fontSize: '0.612rem', color: '#666' }}>
             {selectedItems.size} of {filteredContent.length} selected
           </span>
         </div>
         
         <div style={{ display: 'flex', gap: '0.34rem' }}>
           {activeTab === 'pending' ? (
             <>
               <button
                 onClick={() => handleApproveItems(Array.from(selectedItems))}
                 disabled={selectedItems.size === 0 || isLoading}
                 style={{
                   padding: '0.5rem 1rem',
                   backgroundColor: selectedItems.size > 0 ? '#28a745' : '#ccc',
                   color: 'white',
                   border: 'none',
                   borderRadius: '2.72px',
                   cursor: selectedItems.size > 0 ? 'pointer' : 'not-allowed',
                   fontSize: '0.612rem'
                 }}
               >
                 ✅ Approve Selected ({selectedItems.size})
               </button>
               
               <button
                 onClick={() => handleRejectItems(Array.from(selectedItems))}
                 disabled={selectedItems.size === 0 || isLoading}
                 style={{
                   padding: '0.5rem 1rem',
                   backgroundColor: selectedItems.size > 0 ? '#dc3545' : '#ccc',
                   color: 'white',
                   border: 'none',
                   borderRadius: '2.72px',
                   cursor: selectedItems.size > 0 ? 'pointer' : 'not-allowed',
                   fontSize: '0.612rem'
                 }}
               >
                 ❌ Reject Selected ({selectedItems.size})
               </button>
             </>
           ) : (
             <>
               <button
                 onClick={() => handleArchiveItems(Array.from(selectedItems))}
                 disabled={selectedItems.size === 0 || isLoading}
                 style={{
                   padding: '0.5rem 1rem',
                   backgroundColor: selectedItems.size > 0 ? '#ffc107' : '#ccc',
                   color: selectedItems.size > 0 ? '#212529' : 'white',
                   border: 'none',
                   borderRadius: '2.72px',
                   cursor: selectedItems.size > 0 ? 'pointer' : 'not-allowed',
                   fontSize: '0.612rem'
                 }}
               >
                 📦 Archive Selected ({selectedItems.size})
               </button>
               
               <button
                 onClick={() => handleDeleteItems(Array.from(selectedItems))}
                 disabled={selectedItems.size === 0 || isLoading}
                 style={{
                   padding: '0.5rem 1rem',
                   backgroundColor: selectedItems.size > 0 ? '#dc3545' : '#ccc',
                   color: 'white',
                   border: 'none',
                   borderRadius: '2.72px',
                   cursor: selectedItems.size > 0 ? 'pointer' : 'not-allowed',
                   fontSize: '0.612rem'
                 }}
               >
                 🗑️ Delete Selected ({selectedItems.size})
               </button>
             </>
           )}
         </div>
       </div>
     )}

     {/* Content Display */}
     {isLoading ? (
       <div style={{ textAlign: 'center', padding: '1.36rem', fontSize: '0.748rem', color: '#666' }}>
         Loading content from Firebase...
       </div>
     ) : (
       <div>
         {/* Redundancies View */}
         {activeTab === 'redundancies' ? (
           <div>
             {filteredContent.length === 0 ? (
               <div style={{ 
                 textAlign: 'center', 
                 padding: '2.04rem', 
                 color: '#666',
                 backgroundColor: '#f8f9fa',
                 borderRadius: '5.44px'
               }}>
                 <h3 style={{ margin: '0 0 0.34rem 0' }}>✅ No Redundancies Found</h3>
                 <p style={{ margin: 0 }}>All content appears to be unique!</p>
               </div>
             ) : (
               <div style={{ display: 'flex', flexDirection: 'column', gap: '1.36rem' }}>
                 {filteredContent.map((group) => (
                   <div key={group.id} style={{
                     border: '2px solid #ffc107',
                     borderRadius: '8.16px',
                     padding: '1.02rem',
                     backgroundColor: '#fff8e1'
                   }}>
                     {/* Redundancy Group Header */}
                     <div style={{ marginBottom: '1.02rem' }}>
                       <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.68rem' }}>
                         <div>
                           <h3 style={{ margin: '0 0 0.34rem 0', color: '#f57c00' }}>
                             🚨 Redundancy Group ({group.groupSize} items)
                           </h3>
                           <p style={{ margin: '0 0 0.34rem 0', fontSize: '0.612rem', color: '#666' }}>
                             Similarity Score: {Math.round(group.maxScore * 100)}%
                           </p>
                         </div>
                         
                         <button
                           onClick={() => handleDismissRedundancyGroup(group.id)}
                           style={{
                             padding: '0.5rem 1rem',
                             backgroundColor: '#6c757d',
                             color: 'white',
                             border: 'none',
                             borderRadius: '2.72px',
                             cursor: 'pointer',
                             fontSize: '0.544rem'
                           }}
                         >
                           Dismiss Group
                         </button>
                       </div>
                       
                       {/* Similarity Reasons */}
                       <div style={{ marginBottom: '0.68rem' }}>
                         <strong style={{ fontSize: '0.612rem', color: '#333' }}>Detected Similarities:</strong>
                         <ul style={{ margin: '0.34rem 0', paddingLeft: '1.02rem', fontSize: '0.578rem', color: '#666' }}>
                           {group.reasons.map((reason, index) => (
                             <li key={index}>{reason}</li>
                           ))}
                         </ul>
                       </div>
                       
                       {/* Resolution Actions */}
                       <div style={{ display: 'flex', gap: '0.34rem', flexWrap: 'wrap' }}>
                         <button
                           onClick={() => handleResolveRedundancy(group, 'keep-primary')}
                           style={{
                             padding: '0.5rem 1rem',
                             backgroundColor: '#28a745',
                             color: 'white',
                             border: 'none',
                             borderRadius: '2.72px',
                             cursor: 'pointer',
                             fontSize: '0.544rem'
                           }}
                         >
                           Keep Primary Item
                         </button>
                         
                         <button
                           onClick={() => handleResolveRedundancy(group, 'delete-all')}
                           style={{
                             padding: '0.5rem 1rem',
                             backgroundColor: '#dc3545',
                             color: 'white',
                             border: 'none',
                             borderRadius: '2.72px',
                             cursor: 'pointer',
                             fontSize: '0.544rem'
                           }}
                         >
                           Delete All
                         </button>
                       </div>
                     </div>
                     
                     {/* Items in Group */}
                     <div style={{ 
                       display: 'grid', 
                       gridTemplateColumns: 'repeat(auto-fit, minmax(190.4px, 1fr))', 
                       gap: '0.68rem' 
                     }}>
                       {/* Primary Item */}
                       <div style={{
                         border: '2px solid #28a745',
                         borderRadius: '5.44px',
                         padding: '0.68rem',
                         backgroundColor: 'white'
                       }}>
                         <div style={{ 
                           display: 'flex', 
                           justifyContent: 'space-between', 
                           alignItems: 'center',
                           marginBottom: '0.51rem'
                         }}>
                           <span style={{ 
                             backgroundColor: '#28a745', 
                             color: 'white', 
                             padding: '0.17rem 0.34rem', 
                             borderRadius: '2.72px', 
                             fontSize: '0.51rem',
                             fontWeight: 'bold'
                           }}>
                             PRIMARY
                           </span>
                           
                           <button
                             onClick={() => handleResolveRedundancy(group, 'keep-selected', group.primary.id)}
                             style={{
                               padding: '0.17rem 0.34rem',
                               backgroundColor: '#007bff',
                               color: 'white',
                               border: 'none',
                               borderRadius: '2.72px',
                               cursor: 'pointer',
                               fontSize: '0.51rem'
                             }}
                           >
                             Keep This One
                           </button>
                         </div>
                         
                         <img 
                           src={group.primary.image_url || group.primary.imageUrl} 
                           alt={group.primary.caption || group.primary.title}
                           style={{
                             width: '100%',
                             height: '102px',
                             objectFit: 'cover',
                             borderRadius: '4.08px',
                             marginBottom: '0.51rem'
                           }}
                         />
                         
                         <h4 style={{ margin: '0 0 0.34rem 0', fontSize: '0.612rem' }}>
                           {group.primary.caption || group.primary.title}
                         </h4>
                         
                         <div style={{ fontSize: '0.51rem', color: '#666', marginBottom: '0.34rem' }}>
                           <strong>Source:</strong> {group.primary.user_or_vendor || group.primary.source || 'Unknown'}
                         </div>
                         
                         <div style={{ fontSize: '0.51rem', color: '#666' }}>
                           <strong>Status:</strong> {group.primary.status || 'pending'}
                         </div>
                       </div>
                       
                       {/* Duplicate Items */}
                       {group.duplicates.map((duplicate) => (
                         <div key={duplicate.item.id} style={{
                           border: '2px solid #ffc107',
                           borderRadius: '5.44px',
                           padding: '0.68rem',
                           backgroundColor: 'white'
                         }}>
                           <div style={{ 
                             display: 'flex', 
                             justifyContent: 'space-between', 
                             alignItems: 'center',
                             marginBottom: '0.51rem'
                           }}>
                             <span style={{ 
                               backgroundColor: '#ffc107', 
                               color: '#212529', 
                               padding: '0.17rem 0.34rem', 
                               borderRadius: '2.72px', 
                               fontSize: '0.51rem',
                               fontWeight: 'bold'
                             }}>
                               DUPLICATE ({Math.round(duplicate.similarity.score * 100)}%)
                             </span>
                             
                             <button
                               onClick={() => handleResolveRedundancy(group, 'keep-selected', duplicate.item.id)}
                               style={{
                                 padding: '0.17rem 0.34rem',
                                 backgroundColor: '#007bff',
                                 color: 'white',
                                 border: 'none',
                                 borderRadius: '2.72px',
                                 cursor: 'pointer',
                                 fontSize: '0.51rem'
                               }}
                             >
                               Keep This One
                             </button>
                           </div>
                           
                           <img 
                             src={duplicate.item.image_url || duplicate.item.imageUrl} 
                             alt={duplicate.item.caption || duplicate.item.title}
                             style={{
                               width: '100%',
                               height: '102px',
                               objectFit: 'cover',
                               borderRadius: '4.08px',
                               marginBottom: '0.51rem'
                             }}
                           />
                           
                           <h4 style={{ margin: '0 0 0.34rem 0', fontSize: '0.612rem' }}>
                             {duplicate.item.caption || duplicate.item.title}
                           </h4>
                           
                           <div style={{ fontSize: '0.51rem', color: '#666', marginBottom: '0.34rem' }}>
                             <strong>Source:</strong> {duplicate.item.user_or_vendor || duplicate.item.source || 'Unknown'}
                           </div>
                           
                           <div style={{ fontSize: '0.51rem', color: '#666', marginBottom: '0.34rem' }}>
                             <strong>Status:</strong> {duplicate.item.status || 'pending'}
                           </div>
                           
                           <div style={{ fontSize: '0.476rem', color: '#888' }}>
                             <strong>Match Reasons:</strong>
                             <ul style={{ margin: '0.17rem 0 0 0', paddingLeft: '0.68rem' }}>
                               {duplicate.similarity.reasons.slice(0, 2).map((reason, reasonIndex) => (
                                 <li key={reasonIndex}>{reason}</li>
                               ))}
                             </ul>
                           </div>
                         </div>
                       ))}
                     </div>
                   </div>
                 ))}
               </div>
             )}
           </div>
         ) : (
           /* Regular Content Grid */
           <div style={{ 
             display: 'grid', 
             gridTemplateColumns: 'repeat(auto-fill, minmax(204px, 1fr))', 
             gap: '0.68rem' 
           }}>
             {filteredContent.map((item) => (
               <div key={item.id} style={{
                 border: '1px solid #ddd',
                 borderRadius: '5.44px',
                 padding: '0.68rem',
                 backgroundColor: 'white',
                 position: 'relative',
                 boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
               }}>
                 {/* Selection checkbox */}
                 <input
                   type="checkbox"
                   checked={selectedItems.has(item.id)}
                   onChange={() => handleItemSelect(item.id)}
                   style={{
                     position: 'absolute',
                     top: '0.34rem',
                     left: '0.34rem',
                     transform: 'scale(1.2)',
                     zIndex: 1
                   }}
                 />

                 {/* Content image */}
                 <div style={{ marginBottom: '0.51rem', marginTop: '1.02rem' }}>
                   <img 
                     src={item.image_url || item.imageUrl} 
                     alt={item.caption || item.title}
                     style={{
                       width: '100%',
                       height: '340px',
                       objectFit: 'cover',
                       borderRadius: '4.08px',
                       backgroundColor: '#f8f9fa'
                     }}
                     onError={(e) => {
                       e.target.style.display = 'none';
                     }}
                   />
                 </div>
                 
                 {/* Content details with inline editing for approved content */}
                 <div style={{ marginBottom: '0.51rem' }}>
                   {/* Editable Title - only for approved content */}
                   <div style={{ marginBottom: '0.51rem' }}>
                     {activeTab === 'approved' ? (
                       <div>
                         <label style={{ 
                           fontSize: '0.544rem', 
                           color: '#666', 
                           fontWeight: '600',
                           display: 'block',
                           marginBottom: '0.17rem'
                         }}>
                           Title (click to edit):
                         </label>
                         <InlineEdit
                           value={item.title || item.caption}
                           onSave={(newValue) => handleEditSave(item.id, 'title', newValue)}
                           placeholder="Click to add title..."
                           fieldName="title"
                         />
                       </div>
                     ) : (
                       <h4 style={{ 
                         margin: '0 0 0.34rem 0', 
                         fontSize: '0.68rem', 
                         fontWeight: '600',
                         lineHeight: '1.3'
                       }}>
                         {item.caption || item.title || 'No title'}
                       </h4>
                     )}
                   </div>
                   
                   {/* Editable Description - only for approved content */}
                   <div style={{ marginBottom: '0.51rem' }}>
                     {activeTab === 'approved' ? (
                       <div>
                         <label style={{ 
                           fontSize: '0.544rem', 
                           color: '#666', 
                           fontWeight: '600',
                           display: 'block',
                           marginBottom: '0.17rem'
                         }}>
                           Description (click to edit):
                         </label>
                         <InlineEdit
                           value={item.description}
                           onSave={(newValue) => handleEditSave(item.id, 'description', newValue)}
                           placeholder="Click to add description..."
                           fieldName="description"
                           multiline={true}
                         />
                       </div>
                     ) : (
                       <p style={{ 
                         margin: '0 0 0.34rem 0', 
                         fontSize: '0.578rem', 
                         color: '#666',
                         lineHeight: '1.4',
                         display: '-webkit-box',
                         WebkitLineClamp: 2,
                         WebkitBoxOrient: 'vertical',
                         overflow: 'hidden'
                       }}>
                         {item.description}
                       </p>
                     )}
                   </div>
                   
                   <div style={{ fontSize: '0.544rem', color: '#888', marginBottom: '0.34rem' }}>
                     <strong>Source:</strong> {item.user_or_vendor || item.source || 'Unknown'}
                   </div>
                   
                   <div style={{ fontSize: '0.544rem', color: '#888', marginBottom: '0.34rem' }}>
                     <strong>Category:</strong> {item.category}
                   </div>
                   
                   <div style={{ fontSize: '0.544rem', color: '#888', marginBottom: '0.34rem' }}>
                     <strong>Status:</strong> 
                     <span style={{ 
                       marginLeft: '0.34rem',
                       padding: '0.136rem 0.34rem',
                       borderRadius: '2.72px',
                       fontSize: '0.476rem',
                       fontWeight: '600',
                       backgroundColor: item.status === 'approved' ? '#d4edda' : '#fff3cd',
                       color: item.status === 'approved' ? '#155724' : '#856404'
                     }}>
                       {item.status || 'pending'}
                     </span>
                   </div>
                   
                   <div style={{ fontSize: '0.544rem', color: '#888', marginBottom: '0.34rem' }}>
                     <strong>Firebase ID:</strong> 
                     <span style={{ 
                       marginLeft: '0.34rem',
                       fontFamily: 'monospace',
                       fontSize: '0.476rem',
                       color: '#007bff'
                     }}>
                       {item.id}
                     </span>
                   </div>
                   
                   {/* AI Analysis Info */}
                   {item.aiAnalysis && (
                     <div style={{ fontSize: '0.51rem', color: '#007bff', marginBottom: '0.34rem' }}>
                       <strong>AI Analysis:</strong> {item.aiAnalysis.analysisMethod || 'claude_basic'}
                       {item.aiAnalysis.chatGPTEnhanced && (
                         <span style={{ color: '#28a745', marginLeft: '0.34rem' }}>
                           ✅ ChatGPT Enhanced
                         </span>
                       )}
                     </div>
                   )}
                   
                   {item.tags && item.tags.length > 0 && (
                     <div style={{ fontSize: '0.51rem', color: '#999', marginBottom: '0.34rem' }}>
                       <strong>Tags:</strong> {item.tags.slice(0, 5).join(', ')}
                       {item.tags.length > 5 && ' ...'}
                     </div>
                   )}
                   
                   {item.signals && item.signals.length > 0 && (
                     <div style={{ fontSize: '0.51rem', color: '#999', marginBottom: '0.34rem' }}>
                       <strong>Signals:</strong> {item.signals.slice(0, 5).join(', ')}
                       {item.signals.length > 5 && ' ...'}
                     </div>
                   )}
                   
                   <div style={{ fontSize: '0.476rem', color: '#aaa' }}>
                     Created: {item.createdAt?.toLocaleDateString?.() || 'Unknown'}
                     {item.approvedAt && (
                       <span> • Approved: {new Date(item.approvedAt.seconds * 1000).toLocaleDateString()}</span>
                     )}
                   </div>
                 </div>
                 
                 {/* Enhanced Analysis Panel - show for pending admin uploads and artifex uploads */}
                 {(activeTab === 'pending' &&
                   (item.source === 'uploaded by artifex' || item.submissionType === 'admin_upload') &&
                   item.aiAnalysis?.canEnhanceWithChatGPT) && (
                   <EnhancedAnalysisPanel 
                     item={item} 
                     onAnalysisComplete={loadContentData}
                   />
                 )}
                 
                 {/* Action buttons */}
                 <div style={{ display: 'flex', gap: '0.34rem', marginTop: '0.68rem' }}>
                   {item.sourceUrl && (
                     <button
                       onClick={() => window.open(item.sourceUrl, '_blank')}
                       style={{
                         flex: 1,
                         padding: '0.34rem',
                         backgroundColor: '#17a2b8',
                         color: 'white',
                         border: 'none',
                         borderRadius: '2.72px',
                         cursor: 'pointer',
                         fontSize: '0.544rem'
                       }}
                     >
                       🔗 View Source
                     </button>
                   )}
                   
                   {activeTab === 'pending' ? (
                     <>
                       <button
                         onClick={() => handleApproveItems([item.id])}
                         disabled={isLoading}
                         style={{
                           flex: 1,
                           padding: '0.34rem',
                           backgroundColor: '#28a745',
                           color: 'white',
                           border: 'none',
                           borderRadius: '2.72px',
                           cursor: 'pointer',
                           fontSize: '0.544rem'
                         }}
                       >
                         ✅ Approve
                       </button>
                       
                       <button
                         onClick={() => handleRejectItems([item.id])}
                         disabled={isLoading}
                         style={{
                           padding: '0.34rem',
                           backgroundColor: '#dc3545',
                           color: 'white',
                           border: 'none',
                           borderRadius: '2.72px',
                           cursor: 'pointer',
                           fontSize: '0.544rem'
                         }}
                       >
                         ❌
                       </button>
                     </>
                   ) : (
                     <>
                       <button
                         onClick={() => handleArchiveItems([item.id])}
                         disabled={isLoading}
                         style={{
                           flex: 1,
                           padding: '0.34rem',
                           backgroundColor: '#ffc107',
                           color: '#212529',
                           border: 'none',
                           borderRadius: '2.72px',
                           cursor: 'pointer',
                           fontSize: '0.544rem'
                         }}
                       >
                         📦 Archive
                       </button>
                       
                       <button
                         onClick={() => handleDeleteItems([item.id])}
                         disabled={isLoading}
                         style={{
                           padding: '0.34rem',
                           backgroundColor: '#dc3545',
                           color: 'white',
                           border: 'none',
                           borderRadius: '2.72px',
                           cursor: 'pointer',
                           fontSize: '0.544rem'
                         }}
                       >
                         🗑️
                       </button>
                     </>
                   )}
                 </div>
               </div>
             ))}
           </div>
         )}
         
         {/* Empty state for regular content */}
         {activeTab !== 'redundancies' && filteredContent.length === 0 && (
           <div style={{ 
             textAlign: 'center', 
             padding: '2.04rem', 
             color: '#666',
             backgroundColor: '#f8f9fa',
             borderRadius: '5.44px'
           }}>
             <h3 style={{ margin: '0 0 0.34rem 0' }}>
               {activeTab === 'pending' ? 'No pending content' : 'No approved content'}
             </h3>
             <p style={{ margin: 0 }}>
               {activeTab === 'pending' 
                 ? 'Try fetching new content or check your filters' 
                 : 'No content has been approved yet'}
             </p>
           </div>
         )}
       </div>
     )}

     {/* Status Message */}
     {message && (
       <div style={{
         marginTop: '0.68rem',
         padding: '0.51rem',
         backgroundColor: message.includes('❌') ? '#fee' : '#efe',
         color: message.includes('❌') ? '#c33' : '#363',
         borderRadius: '4.08px',
         fontSize: '0.612rem'
       }}>
         {message}
       </div>
     )}

     {/* Enhanced System Info */}
     <div style={{
       marginTop: '1.36rem',
       padding: '0.68rem',
       backgroundColor: '#e8f5e8',
       border: '1px solid #4caf50',
       borderRadius: '4.08px',
       fontSize: '0.578rem',
       color: '#2e7d32'
     }}>
       <strong>🚀 NEW: Enhanced AI Analysis System!</strong>
       <ul style={{ margin: '0.34rem 0 0 0', paddingLeft: '1.02rem' }}>
         <li>✅ <strong>Enhanced Claude 4.0</strong> with improved design prompts for better descriptions</li>
         <li>✅ <strong>ChatGPT Vision backup</strong> - manually enhance any admin upload for $0.01</li>
         <li>✅ <strong>Cost-aware UI</strong> - see exactly when you're using paid APIs</li>
         <li>✅ <strong>Hybrid approach</strong> - Claude first (free), ChatGPT when you need detail</li>
         <li>✅ <strong>Analysis tracking</strong> - see which AI method was used for each item</li>
       </ul>
     </div>

     {/* Firebase System Info */}
     <div style={{
       marginTop: '0.68rem',
       padding: '0.68rem',
       backgroundColor: '#d1ecf1',
       border: '1px solid #bee5eb',
       borderRadius: '4.08px',
       fontSize: '0.578rem',
       color: '#0c5460'
     }}>
       <strong>🔥 Firebase Database System:</strong> All changes use REAL Firebase document IDs. 
       The approval bug has been resolved - items correctly move from pending to approved status!
       <br /><br />
       <strong>✏️ Inline Editing Feature:</strong> Click any title or description in the approved content tab to edit them directly. 
       Changes save immediately to Firebase and appear instantly in your live discover feed!
     </div>
   </div>
 );
};

export default DiscoverCuration;